/**
 * The one checkbox visual in Poiesis. Used by the boolean cell widget
 * (read-only indicator) AND the primary column's row-select control
 * (interactive) — so there is a single source for "what a checkbox looks like"
 * and the two can't drift apart.
 *
 *  - read-only:    <Checkbox checked={...} />
 *  - interactive:  <Checkbox checked={...} onToggle={...} />  (stops propagation)
 *  - size/context: pass `className` (e.g. h-[18px] w-[18px] to match a glyph slot)
 */
export function Checkbox({
  checked,
  indeterminate = false,
  onToggle,
  className,
  ariaLabel,
  ariaLabelledBy,
}: {
  checked: boolean
  indeterminate?: boolean
  onToggle?: () => void
  className?: string
  /** Accessible name (SEN-1330): a standalone checkbox (terms, verb grant) needs one —
   *  the row-select and boolean-cell uses inherit theirs from the surrounding row/cell. */
  ariaLabel?: string
  ariaLabelledBy?: string
}) {
  const filled = checked || indeterminate
  const interactive = !!onToggle
  return (
    <span
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      tabIndex={interactive ? 0 : undefined}
      onClick={
        interactive
          ? (e) => {
              e.stopPropagation()
              onToggle!()
            }
          : undefined
      }
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault()
                e.stopPropagation()
                onToggle!()
              }
            }
          : undefined
      }
      className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-all duration-100 text-[10px] leading-none ${
        filled ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-transparent text-transparent'
      } ${interactive ? 'cursor-pointer hover:border-primary/60' : ''} ${className ?? ''}`}
    >
      {indeterminate ? '–' : '✓'}
    </span>
  )
}
